import { useCallback, useEffect, useRef } from "react";

export interface IInfiniteScrollFetchOptions {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => Promise<unknown>;
    scrollThresholdPx?: number;
}

/**
 * A custom hook that enables infinite scroll functionality by automatically fetching the next page
 * of data when the user scrolls near the bottom of a scrollable container.
 *
 * @template T - The type of HTML element used as the scroll container (must extend HTMLElement)
 *
 * @param {IInfiniteScrollFetchOptions} options - Configuration options for infinite scrolling
 * @param {boolean} options.hasNextPage - Indicates if there are more pages to fetch (from react-query's useInfiniteQuery)
 * @param {boolean} options.isFetchingNextPage - Indicates if a page is currently being fetched (from react-query's useInfiniteQuery)
 * @param {() => Promise<unknown>} options.fetchNextPage - Function to fetch the next page (from react-query's useInfiniteQuery)
 * @param {number} [options.scrollThresholdPx=100] - Distance in pixels from the bottom to trigger fetch (default: 100px)
 *
 * @returns {React.RefObject<T>} A ref object that should be attached to the scrollable container element
 *
 * @example
 * const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({...});
 * const scrollRef = useInfiniteScrollFetch<HTMLDivElement>({
 *   hasNextPage,
 *   isFetchingNextPage,
 *   fetchNextPage,
 *   scrollThresholdPx: 150
 * });
 *
 * return <div ref={scrollRef} style={{ overflow: 'auto', height: '400px' }}>
 *   {data?.pages.map(page => ...)}
 * </div>
 */
export const useInfiniteScrollFetch = <T extends HTMLElement>({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollThresholdPx = 100,
}: IInfiniteScrollFetchOptions) => {
    const scrollContainerRef = useRef<T>(null);

    const loadNextPage = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage().catch(() => {
                // errors are already handled by react-query
            });
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) {
            return;
        }

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < scrollThresholdPx;

            if (isNearBottom) {
                loadNextPage();
            }
        };

        scrollContainer.addEventListener("scroll", handleScroll);
        return () => {
            scrollContainer.removeEventListener("scroll", handleScroll);
        };
    }, [loadNextPage, scrollThresholdPx]);

    return scrollContainerRef;
};
